import { useEffect, useRef, useState } from "react";
import type { CalendarImpl } from "@fullcalendar/core/internal";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import FullCalendar from "@fullcalendar/react";
import { utcToZonedTime } from "date-fns-tz";
import { setFullCalendarPtLocale } from "@/react/config/Locales/FullCalendar";
import {
  useSmOrMdScreenMediaQuery,
  useTwoExtraLargeScreenMediaQuery,
} from "@/react/hooks/useMediaQuery";
import { useBoolean } from "@circle-react/hooks/utils/useBoolean";
import type { Event } from "@circle-react/types/Event";
import { CalendarHeader } from "./CalendarHeader";
import { renderEventContent } from "./EventContent";
import { EventsModal } from "./EventsModal";
import { MobileViewList } from "./MobileView";
import { checkIfOneDayEvent } from "./helper";
import "./overrides.scss";

interface EventCalendarProps {
  events: Event[];
  fetchNextPage: () => void;
  fetchPrevPage: () => void;
  onChange: (event: Event) => Promise<void>;
  onDestroy: (eventId: number) => void;
}

interface FullCalendarProps extends FullCalendar {
  getApi: () => CalendarImpl;
}

export const EventsCalendar = ({
  events,
  onChange,
  onDestroy,
  fetchNextPage,
  fetchPrevPage,
}: EventCalendarProps) => {
  const calendarRef = useRef<FullCalendarProps>(null);
  const [currentMonth, setCurrentMonth] = useState<string | null>(null);
  const [shouldShowModal, toggleShowModal] = useBoolean(false);
  const isSmallScreen = useSmOrMdScreenMediaQuery();
  const isTwoExtraLargeScreen = useTwoExtraLargeScreenMediaQuery();
  const [selectedEvents, setSelectedEvents] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedEvent, setSelectedEvent] = useState<Event | object>({
    id: null,
  });
  const [eventModalDate, setEventModalDate] = useState<Date>(new Date());

  const getDayMaxEvents = () => {
    if (isSmallScreen || isTwoExtraLargeScreen) {
      return 3;
    }
    return 2;
  };

  const parsedEvents = events.map(event => {
    const {
      event_setting_attributes: {
        starts_at: startsAt,
        ends_at: endsAt,
        time_zone: timeZone,
      },
    } = event || {};

    const isOneDayEvent = checkIfOneDayEvent({ startsAt, endsAt, timeZone });

    return {
      title: event.display_title,
      start: utcToZonedTime(startsAt, timeZone),
      end: utcToZonedTime(endsAt, timeZone),
      multiday: isOneDayEvent ? 1 : 0, //Sorting multiday events first
      event,
    };
  });

  const goPrev = () => {
    const calendarApi = calendarRef.current?.getApi();
    if (!calendarApi) return;
    calendarApi.prev();
    fetchPrevPage();
    setTitleAndRefetch(calendarApi);
  };

  const goNext = () => {
    const calendarApi = calendarRef.current?.getApi();
    if (!calendarApi) return;
    calendarApi.next();
    fetchNextPage();
    setTitleAndRefetch(calendarApi);
  };

  const setTitleAndRefetch = (calendarApi: any) => {
    const data = calendarApi?.getCurrentData();
    setCurrentMonth(data?.viewTitle);
    calendarApi.refetchEvents();
  };

  const setInitialData = async (calendarApi: any) => {
    await setFullCalendarPtLocale(calendarApi);
    const data = calendarApi?.getCurrentData();
    setCurrentMonth(data?.viewTitle);
  };

  useEffect(() => {
    if (calendarRef.current) {
      const calendarApi = calendarRef.current?.getApi();
      void setInitialData(calendarApi);
    }
  }, [calendarRef.current]);

  const handleChange = async (props: any) => {
    await onChange(props).then(() => {
      const calendarApi = calendarRef.current?.getApi();
      if (!calendarApi) return;
      calendarApi.refetchEvents();
    });
  };

  return (
    <div id="calendar-root">
      <CalendarHeader
        currentMonth={currentMonth}
        goPrev={goPrev}
        goNext={goNext}
      />
      <FullCalendar
        ref={calendarRef}
        eventDisplay={isSmallScreen ? "list-item" : "auto"}
        plugins={[interactionPlugin, dayGridPlugin]}
        initialView="dayGridMonth"
        weekends
        events={parsedEvents}
        dateClick={info => {
          if (!isSmallScreen) return;
          setSelectedDate(info.date);
          const calendarApi = calendarRef.current?.getApi();
          if (!calendarApi) return;
          calendarApi.select(info.dateStr);
        }}
        eventContent={info =>
          renderEventContent({
            eventInfo: info,
            isSmallScreen,
            onChange,
            selectedEvent,
            setSelectedEvent,
          })
        }
        dayHeaderContent={date =>
          isSmallScreen ? date.text.slice(0, 1) : date.text
        }
        eventClick={info => {
          info.jsEvent.preventDefault();
          setSelectedEvent(info.event.extendedProps.event);
        }}
        eventOrder={["multiday", "start", "-duration", "title"]}
        eventOrderStrict
        themeSystem="standard"
        dayMaxEvents={getDayMaxEvents()}
        headerToolbar={false}
        eventTimeFormat={{
          hour: "numeric",
          minute: "2-digit",
          meridiem: "short",
        }}
        moreLinkClick={info => {
          info.jsEvent.preventDefault(); // don't let the browser navigate
          const eventItems: any =
            info?.allSegs?.map(seg => seg?.event?.extendedProps.event) || [];
          setSelectedEvents(eventItems);
          const firstEventStart =
            eventItems[0]?.event_setting_attributes?.starts_at;
          const eventDate = firstEventStart
            ? new Date(firstEventStart)
            : info.date;

          setEventModalDate(eventDate);
          toggleShowModal();
        }}
        height="auto"
        contentHeight={isSmallScreen ? 500 : 612}
        moreLinkContent={renderMoreLinkContent}
        eventClassNames="custom-event group"
      />
      <EventsModal
        events={selectedEvents}
        allEvents={events}
        isOpen={shouldShowModal}
        onClose={toggleShowModal}
        date={eventModalDate.toDateString()}
        onChange={handleChange}
      />
      {isSmallScreen && (
        <MobileViewList
          date={selectedDate}
          events={events}
          onChange={onChange}
          onDestroy={onDestroy}
        />
      )}
    </div>
  );
};

const renderMoreLinkContent = (info: any) => (
  <span className="text-light hover:text-circle ml-1.5 text-xs font-semibold hover:bg-transparent">{`${info.num} more`}</span>
);
